var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _class, _class2;

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

/**
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Component, Prop } from '@sick-davinci/ce-decorators';
import { style } from '../_style/Common.style';
import { toCssColorValue } from '../_utils/ColorTools.js';
import { AbstractBarChartGrouped } from '../bar-chart/bar-chart-grouped/AbstractBarChartGrouped.js';
import { HistogramLayout } from './HistogramLayout.js';
/**
 * A label for a bar of a histogram
 */

export
/**
 * A histogram.
 *
 * A histogram should be used when the distribution of values is the main concern.
 * If the exact value of a bar is important a bar-chart should be used instead.
 *
 * @example
 * ```html
 * <div style="width: 400px; height: 300px">
 *   <davinci-histogram values="[[4,5,6]]" palette="primary" bar-labels='[{"index": 0, "label": "1"}, {"index": 2, "label": "3"}]'></davinci-histogram>
 * </div>
 * ```
 */
let Histogram = (_dec = Component({
  tag: 'davinci-histogram',
  style
}), _dec2 = Reflect.metadata("design:paramtypes", []), _dec3 = Prop(), _dec4 = Reflect.metadata("design:type", Object), _dec5 = Prop(), _dec6 = Reflect.metadata("design:type", Object), _dec7 = Prop(), _dec8 = Reflect.metadata("design:type", Object), _dec(_class = _dec2(_class = (_class2 = class Histogram extends AbstractBarChartGrouped {
  constructor() {
    super([0]); // default is the first color of the selected palette for all bars

    this._computedLabels = [];
    this._resolvedLabels = [];
    this._userDefinedLabels = null;
    this._contentAreaSize = {
      width: null,
      height: null
    };
  }
  /**
   * Stores the computed labels
   */


  /**
   * The labels to be used for the bars.
   * If not defined, the labels will be computed automatically.
   * Provide an empty array to turn off labels altogether.
   */
  get barLabels() {
    return this._userDefinedLabels;
  }

  set barLabels(labels) {
    this._computedLabels = []; // reset

    this._resolvedLabels = []; // reset

    this._userDefinedLabels = labels;

    this._updateData();

    this._updateStyle();
  }
  /**
   * @override
   */


  _getCategoryLabels() {
    return this._userDefinedLabels ? this._resolveLabels() : this._computeLabels();
  }
  /**
   * The values to be used for the bars of the different data series.
   * A two dimensional array; the first dimension denotes the data series, the second dimension the values for the bars of the corresponding data series.
   * e.g. 'value[0][2,3,4]' stores the values for the bars of the 1st data series.
   * Currently only one data series is supported.
   *
   * @default []
   */


  get values() {
    // funnel the spread values back into one data-series
    const funneledValues = [[]];

    this._getValues().forEach(values => funneledValues[0].push(...values));

    return funneledValues;
  }

  set values(values) {
    if (values.length > 1) {
      // eslint-disable-next-line
      console.warn(`currently only one data series is supported. ${values.length} data series were provided`);
    } // spread the values so one value is equivalent to one bar group


    const spreadValues = [];
    values[0].forEach(value => spreadValues.push([value]));

    this._setValues(spreadValues);
  }
  /**
   * The colors to be used for the data series.
   * The first element denotes the color for the first data series, the second element denotes the color for the second data series and so on.
   *
   * Each entry of this array is either interpreted as an index referring to the selected palette, or a css-color-value designation (e.g. 'purple', '#ff00ff', 'rgba(255,255,128,0.5)').
   *
   * If there are more data series than colors the array will be traversed from the beginning.
   *
   * @default [0] - use the first color of the current color-palette
   */


  get colors() {
    return this._getSeriesColors();
  }

  set colors(value) {
    this._setSeriesColors(value);
  }
  /**
   * Stores data about the content area size
   */


  /**
   * @override
   */
  componentFirstRender() {
    super.componentFirstRender();
    const holder = this.shadowRoot.getElementById('holder');
    holder.addEventListener('ContentAreaSizeChanged', this._handleResize.bind(this), {
      passive: true
    });
  }
  /**
   * Behaviour when the histogram gets resized
   * @param evt the emitted event
   */


  _handleResize(evt) {
    this._contentAreaSize = evt.detail;

    this._updateStyle();
  }
  /**
   * @override
   */


  _createStyle(hostFontFamily) {
    const style = super._createStyle(hostFontFamily);

    style.barSize = this._computeBarSize();
    style.barColors = this._computeBarColors();
    style.minGapBetweenGroups = 0;
    style.maxGapBetweenGroups = 2;
    return style;
  }
  /**
   * Resolves the user defined labels to an array of strings.
   */


  _resolveLabels() {
    var _this$_userDefinedLab;

    const barCount = this.values[0].length;

    if (this._resolvedLabels.length === barCount) {
      return this._resolvedLabels;
    }

    this._resolvedLabels = Array(barCount).fill('');
    (_this$_userDefinedLab = this._userDefinedLabels) === null || _this$_userDefinedLab === void 0 ? void 0 : _this$_userDefinedLab.forEach(userDefinedLabel => {
      if (userDefinedLabel.index >= 0 && userDefinedLabel.index < barCount) {
        this._resolvedLabels[userDefinedLabel.index] = userDefinedLabel.label;
      }
    });
    return this._resolvedLabels;
  }
  /**
   * Computes the size of the bars in pixel.
   *
   * No size less then 1px is allowed.
   *
   * @returns the size to be used for the bars
   */


  _computeBarSize() {
    const barCount = this.values[0].length;
    const relevantContentSize = this.horizontal ? this._contentAreaSize.height : this._contentAreaSize.width;

    if (!relevantContentSize || barCount === 0) {
      // no information about the content size/ no values -> do not compute bar size
      return 0;
    }

    const maxAvailableBarSize = relevantContentSize / barCount; // the size per bar that is maximally available

    if (maxAvailableBarSize > HistogramLayout.MIN_BAR_SIZE_WITH_GAPS) {
      return maxAvailableBarSize * HistogramLayout.PERCENTAGE_BAR_SIZE; // enough space for gaps
    }

    return Math.max(maxAvailableBarSize, 1); // no bar size less than 1px
  }

  _computeLabels() {
    const barCount = this.values[0].length;

    if (this._computedLabels.length === barCount) {
      return this._computedLabels;
    }

    this._computedLabels = [];
    let steps;

    if (barCount < 10) {
      steps = 1;
    } else if (barCount < 50) {
      steps = 5;
    } else if (barCount < 200) {
      steps = 10;
    } else if (barCount < 500) {
      steps = 50;
    } else {
      steps = 100;
    }

    for (let i = 1; i <= barCount; i++) {
      i % steps == 0 ? this._computedLabels.push(i.toString()) : this._computedLabels.push('');
    }

    return this._computedLabels;
  }
  /**
   * Computes the colors for the data series.
   *
   * The first dimension denotes the data series, the second dimension the color.
   *
   * @returns the colors to be used for the data series
   */


  _computeBarColors() {
    return [this._getSeriesColors().map(color => toCssColorValue(color, this.palette))];
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "barLabels", [_dec3, _dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "barLabels"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "values", [_dec5, _dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "values"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "colors", [_dec7, _dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "colors"), _class2.prototype)), _class2)) || _class) || _class);
